import { Card } from '@amelio/ui-library';
import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useParams } from 'react-router-dom';

import { CommentThreadCard } from './components/CommentThreadCard';
import { CommentThreadHeader } from './components/CommentThreadHeader';
import { CommentThreadNavigation } from './components/CommentThreadNavigation';
import EvaluationClosedPage from '../EvaluationClosed/EvaluationClosedPage';

import { isForbiddenError, isNotFoundError, isRefusedError } from '@/api/api';
import { isCommentRepliesDisabledRefusal } from '@/api/review-services/comment-reply/commentRepliesDisabledRefusal';
import { isCommentReplyContentRefusal } from '@/api/review-services/comment-reply/commentReplyContentRefusal';
import {
    useGetCommentThreads,
    useReplyInCommentThread,
} from '@/api/review-services/comment-thread';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { useLinkedThreadId, useUnsentReplies } from '@/components/QuestionResultContainer';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { COMMENT_REPLY_MAX_LENGTH } from '@/constants/review/CommentReply';
import { useAmelioTranslations } from '@/hooks';
import { useRespondentLanguage } from '@/hooks/useRespondentLanguage';
import { useError } from '@/hooks/utils/useError';
import { showErrorToast, showSuccessToast } from '@/plugins/Toast';
import { formatDecimal } from '@/utils/numberFormat';

const PAGE_LAYOUT = 'mx-auto flex w-full max-w-3xl flex-col gap-6 px-6 py-14';

/**
 * Where a respondent reads the replies to the comments they left, and answers them, from the e-mail link
 * without signing in: a 360 respondent, or an employee about their own self-evaluation.
 * The address carries the thread's own key, never the survey link's: a forwarded invitation opens no thread.
 * The server decides who may read and how each reply is signed; this page only shows it.
 */
export const CommentThreadPage = () => {
    const { t, i18n } = useAmelioTranslations();
    const { handleError } = useError();
    const { threadKey = '' } = useParams<{ threadKey: string }>();

    const { data: evaluation, isLoading, error, refetch } = useGetCommentThreads(threadKey);
    const reply = useReplyInCommentThread(threadKey);
    const { unsentReplies, keepUnsentReply } = useUnsentReplies(
        evaluation?.commentRepliesEnabled === false,
    );
    const linkedThreadId = useLinkedThreadId();
    const [chosenIndex, setChosenIndex] = useState<number | null>(null);
    const questionsRef = useRef<HTMLDivElement>(null);
    const questionRefs = useRef<(HTMLDivElement | null)[]>([]);

    // The page speaks the respondent's language, as their survey did.
    const respondentLanguage = useRespondentLanguage({
        isRespondentKnown: Boolean(evaluation),
        surveyLanguage: evaluation?.language,
    });
    const isChangingLanguage = Boolean(respondentLanguage) && respondentLanguage !== i18n.language;

    if (isLoading || isChangingLanguage) {
        return (
            <Skeleton className={PAGE_LAYOUT}>
                <Bone className={'h-24'} />

                <Bone className={'h-64'} />
            </Skeleton>
        );
    }

    // A key that leads nowhere.
    if (isNotFoundError(error)) {
        return <EvaluationClosedPage variant={'commentThreadNotFound'} />;
    }

    // The server refuses an existing thread to anyone signed in but its respondent: a forwarded link, or
    // a shared computer. Saying so tells them what to do, where "not available" would leave them guessing.
    if (isForbiddenError(error)) {
        return <EvaluationClosedPage variant={'commentThreadOfAnotherPerson'} />;
    }

    if (!evaluation) {
        return (
            <EvaluationClosedPage
                variant={'commentThreadLoadError'}
                action={{
                    label: t('reviewAnswer.commentThread.retry'),
                    onClick: () => void refetch(),
                }}
            />
        );
    }

    const replyIn = (reviewQuestionAnswerId: string) => async (content: string) => {
        try {
            await reply.mutateAsync({ reviewQuestionAnswerId, content });
            showSuccessToast({ message: t('reviewAnswer.commentThread.sent') });
        } catch (replyError) {
            if (isCommentRepliesDisabledRefusal(replyError)) {
                showErrorToast({ message: t('reviewAnswer.commentThread.repliesClosedRefused') });
                // Reading the thread again takes the field away: what was typed stays on screen.
                keepUnsentReply(reviewQuestionAnswerId, content);
            } else if (isCommentReplyContentRefusal(replyError)) {
                showErrorToast({
                    message: t('reviewAnswer.commentThread.replyRefused', {
                        maxLength: formatDecimal(COMMENT_REPLY_MAX_LENGTH, i18n.language, {
                            fractionDigits: 0,
                        }),
                    }),
                });
            } else if (isRefusedError(replyError)) {
                showErrorToast({ message: t('reviewAnswer.commentThread.goneRefused') });
            } else {
                handleError({ error: replyError });
            }
            throw replyError;
        }
    };

    const { threads } = evaluation;
    const hasSeveralQuestions = threads.length > 1;
    // Opens on the thread the link names; one it no longer finds opens on the first.
    const linkedIndex = threads.findIndex(
        (thread) => thread.reviewQuestionAnswerId === linkedThreadId,
    );
    const shownIndex = Math.min(chosenIndex ?? Math.max(linkedIndex, 0), threads.length - 1);

    // The buttons sit below the card: on a long one, the next question would open scrolled past its start.
    // Focus moves to the question shown, named by its position: a screen reader says where the reader landed,
    // and focus no longer rests on a button the last question disables.
    const showQuestion = (index: number) => {
        flushSync(() => setChosenIndex(index));
        if (questionsRef.current && questionsRef.current.getBoundingClientRect().top < 0) {
            questionsRef.current.scrollIntoView({ block: 'start' });
        }
        questionRefs.current[index]?.focus({ preventScroll: true });
    };

    return (
        <div className={PAGE_LAYOUT}>
            <CommentThreadHeader evaluation={evaluation} />

            {threads.length === 0 ? (
                <Card
                    className={'rounded-3xl bg-white'}
                    padding={'md'}
                >
                    <EmptyState
                        message={t('reviewAnswer.commentThread.noComments')}
                        className={'py-0'}
                    />
                </Card>
            ) : (
                // Every question stays mounted, one shown at a time: a reply being written survives a look at another.
                <div ref={questionsRef}>
                    {threads.map((thread, index) => {
                        const position = hasSeveralQuestions
                            ? { current: index + 1, total: threads.length }
                            : undefined;

                        return (
                            <div
                                key={thread.reviewQuestionAnswerId}
                                ref={(element) => {
                                    questionRefs.current[index] = element;
                                }}
                                hidden={index !== shownIndex}
                                role={'group'}
                                aria-label={
                                    position &&
                                    t('reviewAnswer.commentThread.questionPosition', position)
                                }
                                tabIndex={-1}
                                className={'focus:outline-none'}
                            >
                                <CommentThreadCard
                                    thread={thread}
                                    evaluation={evaluation}
                                    position={position}
                                    onReply={replyIn(thread.reviewQuestionAnswerId)}
                                    unsentReply={unsentReplies[thread.reviewQuestionAnswerId]}
                                    isReplying={
                                        reply.isPending &&
                                        reply.variables?.reviewQuestionAnswerId ===
                                            thread.reviewQuestionAnswerId
                                    }
                                />
                            </div>
                        );
                    })}
                </div>
            )}

            {hasSeveralQuestions && (
                <CommentThreadNavigation
                    index={shownIndex}
                    total={threads.length}
                    onChange={showQuestion}
                />
            )}
        </div>
    );
};
